// ToolInvocationItem — tool call header; click (or Show all / live focus) reveals result.

import { useEffect, useState, type ReactNode } from 'react';
import type { ProgressStep, SessionEvent } from '@localagent/sdk';
import { formatInlineArgs } from './activity-feed';
import { SubAgentActivityItem } from './SubAgentActivityItem';
import type { SubAgentFeed } from './activity-feed';
import { getToolResultRenderer } from './tool-result-renderers';
import { IMAGE_TOOLS } from './chat-image-scope';
import { llmStatusPhrase, nestedRunName, useNestedLiveCall } from './live-llm-calls';
import { formatElapsed, useNow } from './use-elapsed';
import { ToolImageThumbnails } from './ToolImageThumbnails';

export interface ToolInvocationItemProps {
  step: ProgressStep;
  child?: SubAgentFeed;
  /** Nested feed body when `child` is set (rendered by parent to avoid circular imports). */
  childContent?: ReactNode;
  /**
   * Parent-driven expand (live focus on latest step, or Show all).
   * Clicking the header toggles a local override until the parent focus changes.
   */
  detailsOpen?: boolean;
  events: readonly SessionEvent[];
  getEventData: (seq: number) => Promise<Record<string, unknown>>;
}

function StatusLabel({ status, error }: { status: string | undefined; error: string | undefined }) {
  const isError = status === 'error' || (error != null && error !== '');
  if (!status && !error) {
    return <span className="shrink-0 text-caption text-warning-text">Running…</span>;
  }
  return (
    <span
      data-testid="tool-invocation-status"
      className={`shrink-0 text-caption ${
        isError ? 'text-error-text' : 'text-success-text'
      }`}
    >
      {isError ? status ?? 'error' : status ?? 'success'}
    </span>
  );
}

export function ToolInvocationItem({
  step,
  child,
  childContent,
  detailsOpen = false,
  events,
  getEventData,
}: ToolInvocationItemProps) {
  const toolName = step.toolName ?? 'unknown tool';
  const inFlight = step.toolResultStatus == null && !step.toolResultError;
  const inlineArgs = formatInlineArgs(step.toolArgsPreview ?? '');
  const resultText = step.toolResultPreview ?? step.toolResultError ?? '';
  const isError =
    step.toolResultStatus === 'error' ||
    (step.toolResultError != null && step.toolResultError !== '');
  const ResultRenderer = getToolResultRenderer(toolName);
  const nested = useNestedLiveCall(inFlight ? step.childIdAtStart : undefined);
  const now = useNow(nested != null);

  const [override, setOverride] = useState<boolean | null>(null);
  useEffect(() => {
    setOverride(null);
  }, [detailsOpen]);

  const open = override ?? detailsOpen;

  return (
    <div
      data-testid="tool-invocation"
      data-tool-name={toolName}
      data-details-open={open}
      className="w-full min-w-0 py-0"
    >
      <button
        type="button"
        data-testid="tool-invocation-toggle"
        aria-expanded={open}
        onClick={() => setOverride(!open)}
        className="flex w-full min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-left text-body hover:text-foreground"
      >
        <StatusLabel status={step.toolResultStatus} error={step.toolResultError} />
        <span className="shrink-0 font-medium text-primary">{toolName}</span>
        {inlineArgs ? (
          <span
            data-testid="tool-invocation-args"
            className="min-w-0 truncate font-sans text-foreground/90"
            title={inlineArgs}
          >
            {inlineArgs}
          </span>
        ) : null}
      </button>

      {nested ? (
        <div data-testid="nested-llm-status" data-llm-state={nested.state} className="mt-0.5 min-w-0 pl-1">
          <p
            className={`text-caption ${nested.state === 'stalled' || nested.state === 'failed' ? 'text-error-text' : 'text-muted-foreground'}`}
          >
            {`${nestedRunName(toolName) ?? toolName} · ${llmStatusPhrase(nested)} · ${formatElapsed(nested.elapsedMs + (now - nested.receivedAt))}`}
            {nested.outputChunks > 0 ? ` · ~${nested.outputChunks} tokens` : ''}
          </p>
          {nested.reasoning ? (
            <p data-testid="nested-llm-reasoning" className="line-clamp-4 whitespace-pre-wrap break-words text-caption text-foreground/80">
              {nested.reasoning}
            </p>
          ) : null}
        </div>
      ) : null}

      {IMAGE_TOOLS.has(toolName) ? (
        <ToolImageThumbnails toolName={toolName} step={step} events={events} getEventData={getEventData} />
      ) : null}

      {open && (
        <div className="mt-0.5 min-w-0 pl-1">
          {inFlight ? (
            <p className="text-caption text-muted-foreground">Calling {toolName}…</p>
          ) : (
            <div data-testid="tool-invocation-result" className="min-w-0 text-body">
              {isError ? (
                <p className="whitespace-pre-wrap break-words text-error-text">
                  {resultText || 'Error'}
                </p>
              ) : resultText ? (
                <ResultRenderer text={resultText} toolName={toolName} />
              ) : (
                <p className="text-caption italic text-muted-foreground">No content</p>
              )}
            </div>
          )}

          {child && (
            <SubAgentActivityItem feed={child}>{childContent}</SubAgentActivityItem>
          )}
        </div>
      )}
    </div>
  );
}
